<template>
  <div class="director-edit-form">
    <!-- JSON-driven sections (officerInfo, addresses, appointmentDetails, attachments) -->
    <div v-for="section in resolvedSections" :key="section.id" class="form-section">
      <div class="form-section-header">
        <v-icon v-if="section.icon" size="16" class="mr-2">{{ section.icon }}</v-icon>
        {{ tl(section.title) }}
      </div>
      <div class="form-section-body">
        <SectionRenderer
          :section="section"
          :form-data="formData"
          :readonly="readOnly"
          :ref-data="mergedRefData"
          :resolve-template-fn="resolveTemplate"
          :resolve-group-fn="resolveGroup"
          :show-errors="showErrors"
          density="compact"
          @update:field="handleFieldUpdate"
          @update:address="handleAddressUpdate"
          @field-action="handleFieldAction"
        />
      </div>
    </div>

    <!-- Other Directorship (custom section — not in JSON) -->
    <div class="form-section">
      <div class="form-section-header">
        <v-icon size="16" class="mr-2">mdi-briefcase-outline</v-icon>
        {{ tl('Other Directorship') }}
      </div>
      <div class="form-section-body">
        <div class="directorship-question">
          <span class="text-body-2 font-weight-medium">{{ tl('Does this director has any other directorship') }}</span>
          <v-radio-group
            v-model="hasOtherDirectorship"
            inline
            density="compact"
            :readonly="readOnly"
            hide-details="auto"
            class="mt-2"
          >
            <v-radio :label="tl('YES')" value="YES" color="primary" />
            <v-radio :label="tl('NO')" value="NO" color="primary" />
          </v-radio-group>
        </div>

        <template v-if="hasOtherDirectorship === 'YES'">
          <v-divider class="my-3" />

          <v-tabs v-model="directorshipTab" density="compact" color="primary" class="mb-3">
            <v-tab value="registered">{{ tl('Registered Company') }}</v-tab>
            <v-tab value="group">{{ tl('Group of Companies/Others') }}</v-tab>
          </v-tabs>

          <v-window v-model="directorshipTab">
            <v-window-item value="registered">
              <div class="directorship-entry-form">
                <v-text-field
                  :model-value="directorshipEntry.companyName"
                  :label="tl('Company Name')"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  readonly
                  :placeholder="tl('Click to select entity via AI')"
                  style="cursor: pointer;"
                  bg-color="white"
                  class="mb-2"
                  @click="requestDirectorshipEntity"
                >
                  <template #append-inner>
                    <v-btn
                      icon="mdi-creation"
                      color="primary"
                      size="x-small"
                      variant="text"
                      density="comfortable"
                      @click.stop="requestDirectorshipEntity"
                    />
                  </template>
                </v-text-field>
                <div class="form-grid-2">
                  <v-text-field
                    v-model="directorshipEntry.companyNo"
                    :label="tl('Company No')"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                    readonly
                    class="field-readonly"
                  />
                  <div />
                  <v-menu v-model="dirDateAppointedMenu" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-text-field
                        v-bind="menuProps"
                        :model-value="formatDisplayDate(directorshipEntry.dateAppointed)"
                        :label="tl('Date Appointed')"
                        :placeholder="tl('DD MMM YYYY')"
                        readonly
                        variant="outlined"
                        density="compact"
                        hide-details="auto"
                        bg-color="white"
                      >
                        <template #append-inner><v-icon size="18">mdi-calendar</v-icon></template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      :model-value="ymdToDate(directorshipEntry.dateAppointed)"
                      color="primary"
                      show-adjacent-months
                      @update:model-value="(v: any) => { directorshipEntry.dateAppointed = dateToYmd(v); dirDateAppointedMenu = false; }"
                    />
                  </v-menu>
                  <v-menu v-model="dirDateCeasedMenu" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-text-field
                        v-bind="menuProps"
                        :model-value="formatDisplayDate(directorshipEntry.dateCeased)"
                        :label="tl('Ceased as Director')"
                        :placeholder="tl('DD MMM YYYY')"
                        readonly
                        variant="outlined"
                        density="compact"
                        hide-details="auto"
                        bg-color="white"
                      >
                        <template #append-inner><v-icon size="18">mdi-calendar</v-icon></template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      :model-value="ymdToDate(directorshipEntry.dateCeased)"
                      color="primary"
                      show-adjacent-months
                      @update:model-value="(v: any) => { directorshipEntry.dateCeased = dateToYmd(v); dirDateCeasedMenu = false; }"
                    />
                  </v-menu>
                </div>
              </div>
            </v-window-item>

            <v-window-item value="group">
              <div class="directorship-entry-form">
                <div class="form-grid-2">
                  <v-text-field
                    v-model="directorshipEntry.companyName"
                    :label="tl('Company Name')"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                    bg-color="white"
                    :readonly="readOnly"
                    @update:model-value="(v: string) => { directorshipEntry.companyName = (v || '').toUpperCase(); }"
                  />
                  <v-text-field
                    v-model="directorshipEntry.companyNo"
                    :label="tl('Company No')"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                    bg-color="white"
                    :readonly="readOnly"
                  />
                  <v-menu v-model="dirDateAppointedMenu" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-text-field
                        v-bind="menuProps"
                        :model-value="formatDisplayDate(directorshipEntry.dateAppointed)"
                        :label="tl('Date Appointed')"
                        :placeholder="tl('DD MMM YYYY')"
                        readonly
                        variant="outlined"
                        density="compact"
                        hide-details="auto"
                        bg-color="white"
                      >
                        <template #append-inner><v-icon size="18">mdi-calendar</v-icon></template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      :model-value="ymdToDate(directorshipEntry.dateAppointed)"
                      color="primary"
                      show-adjacent-months
                      @update:model-value="(v: any) => { directorshipEntry.dateAppointed = dateToYmd(v); dirDateAppointedMenu = false; }"
                    />
                  </v-menu>
                  <v-menu v-model="dirDateCeasedMenu" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-text-field
                        v-bind="menuProps"
                        :model-value="formatDisplayDate(directorshipEntry.dateCeased)"
                        :label="tl('Ceased as Director')"
                        :placeholder="tl('DD MMM YYYY')"
                        readonly
                        variant="outlined"
                        density="compact"
                        hide-details="auto"
                        bg-color="white"
                      >
                        <template #append-inner><v-icon size="18">mdi-calendar</v-icon></template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      :model-value="ymdToDate(directorshipEntry.dateCeased)"
                      color="primary"
                      show-adjacent-months
                      @update:model-value="(v: any) => { directorshipEntry.dateCeased = dateToYmd(v); dirDateCeasedMenu = false; }"
                    />
                  </v-menu>
                </div>
              </div>
            </v-window-item>
          </v-window>

          <div class="d-flex justify-end mt-3">
            <v-btn
              color="primary"
              variant="tonal"
              size="small"
              :disabled="!canAddDirectorship || readOnly"
              @click="addDirectorship"
            >
              <v-icon start size="16">mdi-plus</v-icon>
              {{ tl('Add Directorship') }}
            </v-btn>
          </div>

          <div v-if="otherDirectorships.length > 0" class="directorship-table mt-3">
            <div class="directorship-table-header">
              <div class="dth-no">#</div>
              <div class="dth-regNo">{{ tl('Company Reg No') }}</div>
              <div class="dth-name">{{ tl('Company Name') }}</div>
              <div class="dth-appointed">{{ tl('Date Appointed') }}</div>
              <div class="dth-ceased">{{ tl('Date Ceased') }}</div>
              <div class="dth-action"></div>
            </div>
            <div
              v-for="(item, idx) in otherDirectorships"
              :key="idx"
              class="directorship-table-row"
            >
              <div class="dth-no">{{ idx + 1 }}</div>
              <div class="dth-regNo">{{ item.companyNo || '-' }}</div>
              <div class="dth-name">{{ item.companyName || '-' }}</div>
              <div class="dth-appointed">{{ formatDisplayDate(item.dateAppointed) }}</div>
              <div class="dth-ceased">{{ formatDisplayDate(item.dateCeased) }}</div>
              <div class="dth-action">
                <v-btn
                  icon="mdi-delete-outline"
                  size="x-small"
                  variant="text"
                  color="error"
                  :disabled="readOnly"
                  @click="removeDirectorship(idx)"
                />
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted, inject } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { formatDateOnly } from '@/shared/composables/dateUtils';
import SectionRenderer from '../../shared/renderers/SectionRenderer.vue';
import { useFormConfig } from '../composables/useFormConfig';
import type { SectionDef, FieldDef } from '../../shared/composables/useFormRenderer';

// Translation
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage, { silent: true });
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

// Types (exported for parent usage)
export interface DirectorshipEntry {
  companyNo: string;
  companyName: string;
  dateAppointed: string;
  dateCeased: string;
  source: 'registered' | 'group';
}

export interface AppointFormData {
  idType: string;
  idNumber: string;
  name: string;
  designation: string;
  dob: string;
  gender: string;
  race: string;
  nationality: string;
  licenseNo: string;
  businessOccupation: string;
  email: string;
  mobile: string;
  residentialStreet01: string;
  residentialStreet02: string;
  residentialStreet03: string;
  residentialCountry: string;
  residentialState: string;
  residentialCity: string;
  residentialPostcode: string;
  sameAsAbove: boolean;
  serviceStreet01: string;
  serviceStreet02: string;
  serviceStreet03: string;
  serviceCountry: string;
  serviceState: string;
  serviceCity: string;
  servicePostcode: string;
  appointDesignation: string;
  alternateDirectorName: string;
  dateOfAppointment: string;
  hasOtherDirectorship: string;
  otherDirectorships: DirectorshipEntry[];
  resolutionAttachment: File[] | null;
  identificationAttachment: File[] | null;
  _isDirty: boolean;
}

interface Props {
  readOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  readOnly: false,
});

const emit = defineEmits<{
  'update:formData': [data: AppointFormData];
}>();

// ── Form config (JSON-driven) ──
const { getActionSections, enums, refData: baseRefData, resolveTemplate, resolveGroup } = useFormConfig();

// Merge enums into refData so FieldRenderer can use dataSource for enum fields
const mergedRefData = computed(() => ({
  ...baseRefData,
  designations: enums.designations as any[],
  businessOccupations: enums.businessOccupations as any[],
}));

// ── Resolve sections: groups + extraFields → flat fields, apply dynamic labels ──
const resolvedSections = computed((): SectionDef[] => {
  const sections = getActionSections('appoint');
  return sections.map(section => {
    const s = section as any;
    if (!s.groups) return section;

    // Resolve template groups into flat fields
    let fields: FieldDef[] = s.groups.flatMap((g: any) => resolveGroup(g));
    const extraFields: FieldDef[] = s.extraFields || [];
    fields = [...fields, ...extraFields];

    // Apply dynamic label for idNumber based on current idType
    if (section.id === 'officerInfo') {
      const currentIdType = formData['officerInfo.idType'] || '';
      if (currentIdType) {
        const idTypeData = ((baseRefData.idTypes || []) as any[]).find((t: any) => t.value === currentIdType);
        if (idTypeData) {
          fields = fields.map(f => {
            if (f.id === 'idNumber' && f.dynamicLabel) {
              return {
                ...f,
                label: idTypeData.label || f.label,
                placeholder: idTypeData.placeholder || f.placeholder,
                maxlength: idTypeData.maxlength || f.maxlength,
              };
            }
            return f;
          });
        }
      }
    }

    return { ...section, fields } as SectionDef;
  });
});

// ── Form data (flat map: "sectionId.fieldId" → value) ──
const formData: Record<string, any> = reactive({});
const showErrors = ref(false);
let originalSnapshot = '';

function handleFieldUpdate(payload: { sectionId: string; fieldId: string; value: any }) {
  const key = `${payload.sectionId}.${payload.fieldId}`;
  formData[key] = payload.value;
  emitFormData();
}

function handleAddressUpdate(payload: { sectionId: string; addressId: string; value: Record<string, any> }) {
  const key = `${payload.sectionId}._addr.${payload.addressId}`;
  formData[key] = payload.value;
  emitFormData();
}

// ── Field action handler (AI pick buttons) ──
const pickContact = inject<((cb: (contact: any) => void) => void) | null>('pickContact', null);

function handleFieldAction(payload: { sectionId: string; action: string; fieldId: string }) {
  if (payload.action === 'pick-contact') {
    requestContactPick();
  }
}

function requestContactPick() {
  if (props.readOnly) return;
  if (pickContact) {
    pickContact((contact: any) => {
      applyContactData(contact);
    });
  }
}

function applyContactData(c: any) {
  const name = c.name || c.CONTACT_NAME || [c.firstName || c.FIRST_NAME, c.surname || c.SURNAME].filter(Boolean).join(' ');
  if (name) formData['officerInfo.name'] = name.toUpperCase();

  // ── ID Type: use backend idTypeName → match form values, fallback to pattern detection ──
  const regNo = c.contactRegNo || c.CONTACT_REG_NO || '';
  if (regNo) {
    formData['officerInfo.idNumber'] = regNo;
    formData['officerInfo.idType'] = resolveIdType(c) || detectIdType(regNo);
  }

  if (c.dateOfBirth || c.DATE_OF_BIRTH) formData['officerInfo.dob'] = normalizeDateToDisplay(c.dateOfBirth || c.DATE_OF_BIRTH);

  // ── Gender / Race: normalize to uppercase (backend may return lowercase) ──
  const gender = String(c.gender || c.PROFILE_GENDER || '').trim().toUpperCase();
  if (gender) formData['officerInfo.gender'] = gender;

  const race = String(c.race || c.PROFILE_RACE || '').trim().toUpperCase();
  if (race) formData['officerInfo.race'] = race;

  // ── Nationality: resolve from nationalityName/nationalityScraping → form value ──
  const nationality = resolveNationality(c);
  if (nationality) formData['officerInfo.nationality'] = nationality;

  if (c.contactEmail || c.CONTACT_EMAIL) formData['officerInfo.email'] = c.contactEmail || c.CONTACT_EMAIL;
  if (c.mobileNo || c.MOBILE_NO) formData['officerInfo.mobile'] = c.mobileNo || c.MOBILE_NO;
  // ── Business Occupation: only set if value exists in our dropdown list ──
  const rawOccupation = String(c.occupation || c.PROFILE_OCCUPATION || '').trim();
  if (rawOccupation) {
    const occupations = (enums.businessOccupations || []) as { value: string; label: string }[];
    const normalized = rawOccupation.toUpperCase().replace(/[\s-]+/g, '_');
    const matched = occupations.find(o =>
      o.value === normalized || o.label.toUpperCase() === rawOccupation.toUpperCase(),
    );
    if (matched) formData['officerInfo.businessOccupation'] = matched.value;
  }

  // ── Address auto-fill: residentialAddress → addresses._addr.residential ──
  applyAddressData(c.residentialAddress, 'addresses._addr.residential');
  applyAddressData(c.serviceAddress, 'addresses._addr.service');

  emitFormData();
}

/** Map backend idTypeName to form id-types value (e.g. "Army ID" → "ARMY_ID") */
function resolveIdType(c: any): string {
  const idTypeName = String(c.idTypeName || c.ID_TYPE_NAME || '').trim();
  if (!idTypeName) return '';
  const idTypes = (baseRefData.idTypes || []) as { value: string; label: string }[];
  // Normalize: uppercase + replace spaces/hyphens with underscores
  const normalized = idTypeName.toUpperCase().replace(/[\s-]+/g, '_');
  // Match against value directly
  const byValue = idTypes.find(t => t.value === normalized);
  if (byValue) return byValue.value;
  // Match against label (case-insensitive)
  const byLabel = idTypes.find(t => t.label.toUpperCase() === idTypeName.toUpperCase());
  if (byLabel) return byLabel.value;
  return '';
}

/** Resolve nationality from backend fields to form dropdown value (e.g. "MALAYSIAN" → "MALAYSIA") */
function resolveNationality(c: any): string {
  const nationalities = (baseRefData.nationalities || []) as { value: string; label: string }[];
  if (!nationalities.length) return c.nationalityCd || c.NATIONALITY_CD || '';

  const candidates = [
    c.nationalityName || '',
    c.nationalityScraping || '',
  ].map(s => String(s).trim().toUpperCase()).filter(Boolean);

  for (const candidate of candidates) {
    // Exact match against value or label
    const exact = nationalities.find(n => n.value === candidate || n.label === candidate);
    if (exact) return exact.value;

    // Strip common demonym suffixes → country stem, then match
    const stem = candidate
      .replace(/(IAN|AN|ESE|ISH|I|ER)$/i, '')
      .replace(/\s+$/, '');
    if (stem) {
      const byStem = nationalities.find(n =>
        n.value.startsWith(stem) || n.label.startsWith(stem),
      );
      if (byStem) return byStem.value;
    }
  }

  // Fallback: raw code
  return c.nationalityCd || c.NATIONALITY_CD || '';
}

/** Apply address JSON data to form address fields */
function applyAddressData(addr: any, formKey: string) {
  if (!addr || typeof addr !== 'object') return;
  const country = resolveCountryValue(addr.country || '');
  formData[formKey] = {
    street01: String(addr.street01 || '').toUpperCase(),
    street02: String(addr.street02 || '').toUpperCase(),
    street03: String(addr.street03 || '').toUpperCase(),
    postcode: String(addr.post_code || addr.postcode || ''),
    city: String(addr.city || '').toUpperCase(),
    state: resolveStateValue(addr.state || ''),
    country: country || 'MALAYSIA',
  };
}

/** Normalize country code/name to form nationality value (e.g. "MY"/"MYS"/"B15135" → "MALAYSIA") */
function resolveCountryValue(raw: string): string {
  if (!raw) return '';
  const upper = raw.trim().toUpperCase();
  const nationalities = (baseRefData.nationalities || []) as { value: string; label: string }[];
  // Exact match against dropdown values/labels
  const exact = nationalities.find(n => n.value === upper || n.label === upper);
  if (exact) return exact.value;
  // ISO 2/3-letter codes + SSM system basecodes (B15xxx)
  const codeMap: Record<string, string> = {
    MY: 'MALAYSIA', MYS: 'MALAYSIA', SG: 'SINGAPORE', SGP: 'SINGAPORE',
    ID: 'INDONESIA', IDN: 'INDONESIA', CN: 'CHINA', CHN: 'CHINA',
    IN: 'INDIA', IND: 'INDIA', TH: 'THAILAND', THA: 'THAILAND',
    PH: 'PHILIPPINES', PHL: 'PHILIPPINES', VN: 'VIETNAM', VNM: 'VIETNAM',
    JP: 'JAPAN', JPN: 'JAPAN', KR: 'KOREA_SOUTH', KOR: 'KOREA_SOUTH',
    TW: 'TAIWAN', TWN: 'TAIWAN', HK: 'HONG_KONG', HKG: 'HONG_KONG',
    GB: 'UNITED_KINGDOM', GBR: 'UNITED_KINGDOM', US: 'UNITED_STATES_OF_AMERICA', USA: 'UNITED_STATES_OF_AMERICA',
    AU: 'AUSTRALIA', AUS: 'AUSTRALIA', NZ: 'NEW_ZEALAND', NZL: 'NEW_ZEALAND',
    BD: 'BANGLADESH', BGD: 'BANGLADESH', PK: 'PAKISTAN', PAK: 'PAKISTAN',
    MM: 'MYANMAR', MMR: 'MYANMAR', KH: 'CAMBODIA', KHM: 'CAMBODIA',
    BN: 'BRUNEI', BRN: 'BRUNEI', LA: 'LAOS', LAO: 'LAOS',
    // SSM system basecodes (B15xxx series)
    B15101: 'MALAYSIA', B15135: 'MALAYSIA', B15201: 'SINGAPORE',
    B15301: 'BRUNEI', B15401: 'INDONESIA', B15501: 'THAILAND',
    B15601: 'PHILIPPINES', B15701: 'VIETNAM', B15801: 'MYANMAR',
    B15901: 'CAMBODIA', B16001: 'LAOS', B16101: 'CHINA',
    B16201: 'HONG_KONG', B16301: 'TAIWAN', B16401: 'JAPAN',
    B16501: 'KOREA_SOUTH', B16601: 'INDIA', B16701: 'PAKISTAN',
    B16801: 'BANGLADESH', B16901: 'SRI_LANKA',
    B19901: 'UNITED_KINGDOM', B20001: 'UNITED_STATES_OF_AMERICA',
    B20101: 'AUSTRALIA', B20201: 'NEW_ZEALAND',
  };
  return codeMap[upper] || upper;
}

/** Normalize state label from backend to form dropdown value (e.g. "W.P. KUALA LUMPUR" → "WP_KUALA_LUMPUR") */
function resolveStateValue(raw: string): string {
  if (!raw) return '';
  const upper = raw.trim().toUpperCase();
  const states = (baseRefData.malaysianStates || []) as { value: string; label: string }[];
  // Exact match by value
  const byValue = states.find(s => s.value === upper);
  if (byValue) return byValue.value;
  // Match by label (backend may store display name like "W.P. KUALA LUMPUR")
  const byLabel = states.find(s => s.label.toUpperCase() === upper);
  if (byLabel) return byLabel.value;
  // Fallback: normalize to underscore format (strip dots, replace spaces with underscores)
  const normalized = upper.replace(/\./g, '').replace(/\s+/g, '_').replace(/_+/g, '_');
  const byNorm = states.find(s => s.value === normalized);
  if (byNorm) return byNorm.value;
  return upper;
}

function detectIdType(regNo: string): string {
  if (!regNo) return '';
  const cleaned = regNo.replace(/[-\s]/g, '');
  if (/^\d{12}$/.test(cleaned)) return 'NRIC';
  if (/^[A-Z]/i.test(cleaned)) return 'PASSPORT';
  return '';
}

// ── Other Directorship (custom — not in JSON) ──
const pickDirectorshipEntity = inject<((callback: (entity: any) => void) => void) | null>('pickDirectorshipEntity', null);

function requestDirectorshipEntity() {
  if (props.readOnly) return;
  if (pickDirectorshipEntity) {
    pickDirectorshipEntity((entity: any) => {
      const regNo = (entity.registrationNo || entity.regNo || entity.REG_NO || '').toString().trim();
      const regNoOld = (entity.regNoOld || entity.REG_NO_OLD || '').toString().trim();
      const companyName = (entity.companyName || entity.entityName || entity.COMPANY_NAME || '').toString().trim();
      directorshipEntry.companyNo = [regNo, regNoOld].filter(Boolean).join(' / ');
      directorshipEntry.companyName = companyName;
    });
  }
}

const hasOtherDirectorship = ref('NO');
const directorshipTab = ref('registered');
const otherDirectorships = ref<DirectorshipEntry[]>([]);
const directorshipEntry = reactive({
  companyNo: '',
  companyName: '',
  dateAppointed: '',
  dateCeased: '',
});
const dirDateAppointedMenu = ref(false);
const dirDateCeasedMenu = ref(false);

const canAddDirectorship = computed(() => {
  return directorshipEntry.companyNo.trim() || directorshipEntry.companyName.trim();
});

function clearDirectorshipEntry() {
  directorshipEntry.companyNo = '';
  directorshipEntry.companyName = '';
  directorshipEntry.dateAppointed = '';
  directorshipEntry.dateCeased = '';
}

function addDirectorship() {
  if (!canAddDirectorship.value) return;
  otherDirectorships.value.push({
    companyNo: directorshipEntry.companyNo.trim(),
    companyName: directorshipEntry.companyName.trim(),
    dateAppointed: directorshipEntry.dateAppointed,
    dateCeased: directorshipEntry.dateCeased,
    source: directorshipTab.value as 'registered' | 'group',
  });
  clearDirectorshipEntry();
  emitFormData();
}

function removeDirectorship(idx: number) {
  otherDirectorships.value.splice(idx, 1);
  emitFormData();
}

// ── Date helpers (for Other Directorship custom section) ──
function formatDisplayDate(dateStr: string): string {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const [y, m, d] = parts.map(Number);
  if (!y || !m || !d) return dateStr;
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  return `${d} ${months[m - 1]} ${y}`;
}

function ymdToDate(ymd: string): Date | null {
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function dateToYmd(d: Date | null): string {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── Date helpers ──
const MONTH_FULL = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
const MONTH_ABBR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const sessionStore = useSessionStore();

/**
 * Normalize any date format → user's preferred display format via formatDateOnly.
 * Backend contact data may arrive as "1990-01-15T00:00:00.000Z", "1990-01-15", etc.
 * formatDateOnly reads sessionStore.user.user.dateFormat automatically.
 */
function normalizeDateToDisplay(raw: string): string {
  if (!raw) return '';
  const formatted = formatDateOnly(raw);
  return formatted || String(raw).trim();
}

/**
 * Convert any user-formatted date string → "YYYY-MM-DD" for API payload.
 * Handles user's dateFormat (DD/MM/YYYY, MM/DD/YYYY, dd MMM yyyy, etc.) and legacy formats.
 */
function displayDateToYmd(display: string): string {
  if (!display) return '';
  const str = display.trim();
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  // ISO datetime: YYYY-MM-DDTHH:mm:ss...
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  // Month name formats: "9 January 2024", "09 Jan. 2024", "15 JANUARY 1990"
  const monthNameMatch = str.match(/^(\d{1,2})\s+([A-Za-z.]+)\s+(\d{4})$/);
  if (monthNameMatch) {
    const [, d, monthStr, y] = monthNameMatch;
    const mc = monthStr.replace(/\./g, '').toUpperCase();
    let idx = MONTH_FULL.indexOf(mc);
    if (idx < 0) idx = MONTH_ABBR.indexOf(mc);
    if (idx >= 0) {
      return `${y}-${String(idx + 1).padStart(2, '0')}-${String(Number(d)).padStart(2, '0')}`;
    }
  }

  // Numeric formats — use user's dateFormat to determine part order
  const userFormat = (sessionStore.user?.user?.dateFormat || 'DD/MM/YYYY').toUpperCase();
  const sep = str.match(/[/.\-]/)?.[0];
  if (sep) {
    const parts = str.split(sep);
    if (parts.length === 3) {
      let y = '', m = '', d = '';
      if (userFormat.startsWith('YYYY')) { y = parts[0]; m = parts[1]; d = parts[2]; }
      else if (userFormat.startsWith('MM')) { m = parts[0]; d = parts[1]; y = parts[2]; }
      else { d = parts[0]; m = parts[1]; y = parts[2]; } // DD-first default
      if (y && m && d) {
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      }
    }
  }

  // Last resort: Date.parse (timezone-aware, may shift)
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
  }
  return str;
}

// ── Emit: convert flat formData → AppointFormData ──
function getFormSnapshot(): string {
  return JSON.stringify(formData);
}

function checkDirty(): boolean {
  return getFormSnapshot() !== originalSnapshot;
}

function buildEmitData(): AppointFormData {
  const residentialAddr = formData['addresses._addr.residential'] || {};
  const serviceAddr = formData['addresses._addr.service'] || {};
  // Service address is "same as above" if it has no meaningful data or is identical to residential
  const serviceHasContent = Object.entries(serviceAddr).some(
    ([k, v]) => k !== 'country' && v != null && String(v).trim() !== '',
  );
  const isSameAddr = !serviceHasContent || JSON.stringify(residentialAddr) === JSON.stringify(serviceAddr);

  return {
    idType: formData['officerInfo.idType'] || '',
    idNumber: formData['officerInfo.idNumber'] || '',
    name: formData['officerInfo.name'] || '',
    designation: formData['officerInfo.designation'] || '',
    dob: displayDateToYmd(formData['officerInfo.dob'] || ''),
    gender: formData['officerInfo.gender'] || '',
    race: formData['officerInfo.race'] || '',
    nationality: formData['officerInfo.nationality'] || '',
    licenseNo: '',
    businessOccupation: formData['officerInfo.businessOccupation'] || '',
    email: formData['officerInfo.email'] || '',
    mobile: formData['officerInfo.mobile'] || '',
    residentialStreet01: residentialAddr.street01 || '',
    residentialStreet02: residentialAddr.street02 || '',
    residentialStreet03: residentialAddr.street03 || '',
    residentialCountry: residentialAddr.country || 'MALAYSIA',
    residentialState: residentialAddr.state || '',
    residentialCity: residentialAddr.city || '',
    residentialPostcode: residentialAddr.postcode || '',
    sameAsAbove: isSameAddr,
    serviceStreet01: serviceAddr.street01 || '',
    serviceStreet02: serviceAddr.street02 || '',
    serviceStreet03: serviceAddr.street03 || '',
    serviceCountry: serviceAddr.country || 'MALAYSIA',
    serviceState: serviceAddr.state || '',
    serviceCity: serviceAddr.city || '',
    servicePostcode: serviceAddr.postcode || '',
    appointDesignation: formData['appointmentDetails.appointDesignation'] || 'DIRECTOR',
    alternateDirectorName: formData['appointmentDetails.alternateDirectorName'] || '',
    dateOfAppointment: displayDateToYmd(formData['appointmentDetails.dateOfAppointment'] || ''),
    hasOtherDirectorship: hasOtherDirectorship.value,
    otherDirectorships: [...otherDirectorships.value],
    resolutionAttachment: formData['attachments._file.resolutionAttachment'] ? [formData['attachments._file.resolutionAttachment']] : null,
    identificationAttachment: formData['attachments._file.identificationAttachment'] ? [formData['attachments._file.identificationAttachment']] : null,
    _isDirty: checkDirty(),
  };
}

function emitFormData() {
  emit('update:formData', buildEmitData());
}

// ── Watchers ──
watch(formData, () => {
  emitFormData();
}, { deep: true });

watch(hasOtherDirectorship, () => {
  emitFormData();
});

// ── Validation ──
function validate(): boolean {
  const valid = getValidationErrors().length === 0;
  showErrors.value = !valid;
  return valid;
}

function getValidationErrors(): string[] {
  const errors: string[] = [];

  // Personal Information required fields
  if (!formData['officerInfo.name']) errors.push('Full Name is required');
  if (!formData['officerInfo.idType']) errors.push('Identification Type is required');
  if (!formData['officerInfo.idNumber']) errors.push('Identification Number is required');
  if (!formData['officerInfo.dob']) errors.push('Date of Birth is required');
  if (!formData['officerInfo.nationality']) errors.push('Nationality is required');
  if (!formData['officerInfo.gender']) errors.push('Gender is required');
  if (!formData['officerInfo.race']) errors.push('Race is required');
  if (!formData['officerInfo.email']) errors.push('Email is required');
  if (!formData['officerInfo.designation']) errors.push('Designation is required');
  if (!formData['officerInfo.businessOccupation']) errors.push('Business Occupation is required');

  // Residential Address
  const resAddr = formData['addresses._addr.residential'] || {};
  if (!resAddr.street01) errors.push('Residential Address: Street 01 is required');
  if (!resAddr.country) errors.push('Residential Address: Country is required');
  if (!resAddr.state) errors.push('Residential Address: State is required');
  if (!resAddr.city) errors.push('Residential Address: City is required');
  if (!resAddr.postcode) errors.push('Residential Address: Post Code is required');

  // Service Address
  const svcAddr = formData['addresses._addr.service'] || {};
  if (!svcAddr.street01) errors.push('Service Address: Street 01 is required');
  if (!svcAddr.country) errors.push('Service Address: Country is required');
  if (!svcAddr.state) errors.push('Service Address: State is required');
  if (!svcAddr.city) errors.push('Service Address: City is required');
  if (!svcAddr.postcode) errors.push('Service Address: Post Code is required');

  // Appointment Details
  if (!formData['appointmentDetails.dateOfAppointment']) errors.push('Date of Appointment is required');

  // Attachment
  if (!formData['attachments._file.resolutionAttachment']) errors.push('Resolution attachment is required');

  return errors;
}

/**
 * Restore saved AppointFormData back into the form (re-entry flow).
 * Maps flat AppointFormData keys → reactive formData['section.field'] keys.
 */
function loadSavedData(data: Record<string, any>) {
  if (!data) return;

  // Officer Info
  if (data.idType) formData['officerInfo.idType'] = data.idType;
  if (data.idNumber) formData['officerInfo.idNumber'] = data.idNumber;
  if (data.name) formData['officerInfo.name'] = data.name;
  if (data.designation) formData['officerInfo.designation'] = data.designation;
  if (data.dob) formData['officerInfo.dob'] = normalizeDateToDisplay(data.dob);
  if (data.gender) formData['officerInfo.gender'] = data.gender;
  if (data.race) formData['officerInfo.race'] = data.race;
  if (data.nationality) formData['officerInfo.nationality'] = data.nationality;
  if (data.businessOccupation) formData['officerInfo.businessOccupation'] = data.businessOccupation;
  if (data.email) formData['officerInfo.email'] = data.email;
  if (data.mobile) formData['officerInfo.mobile'] = data.mobile;

  // Residential Address
  formData['addresses._addr.residential'] = {
    street01: data.residentialStreet01 || '',
    street02: data.residentialStreet02 || '',
    street03: data.residentialStreet03 || '',
    country: data.residentialCountry || 'MALAYSIA',
    state: data.residentialState || '',
    city: data.residentialCity || '',
    postcode: data.residentialPostcode || '',
  };

  // Service Address
  if (!data.sameAsAbove) {
    formData['addresses._addr.service'] = {
      street01: data.serviceStreet01 || '',
      street02: data.serviceStreet02 || '',
      street03: data.serviceStreet03 || '',
      country: data.serviceCountry || 'MALAYSIA',
      state: data.serviceState || '',
      city: data.serviceCity || '',
      postcode: data.servicePostcode || '',
    };
  }

  // Appointment Details
  if (data.appointDesignation) formData['appointmentDetails.appointDesignation'] = data.appointDesignation;
  if (data.alternateDirectorName) formData['appointmentDetails.alternateDirectorName'] = data.alternateDirectorName;
  if (data.dateOfAppointment) formData['appointmentDetails.dateOfAppointment'] = data.dateOfAppointment;

  // Other Directorship
  hasOtherDirectorship.value = data.hasOtherDirectorship || 'NO';
  if (Array.isArray(data.otherDirectorships)) {
    otherDirectorships.value = [...data.otherDirectorships];
  }

  // Note: File attachments cannot be restored (not serializable)
  originalSnapshot = getFormSnapshot();
}

defineExpose({ validate, getValidationErrors, buildEmitData, loadSavedData });

// ── Initialize ──
onMounted(() => {
  // Initialize defaults from JSON sections
  for (const section of resolvedSections.value) {
    if (section.fields) {
      for (const field of section.fields) {
        const key = `${section.id}.${field.id}`;
        if (formData[key] === undefined) {
          formData[key] = (field as any).default !== undefined ? (field as any).default : '';
        }
      }
    }
  }
  originalSnapshot = getFormSnapshot();
  emit('update:formData', buildEmitData());
});
</script>

<style scoped>
.director-edit-form {
  padding: 4px 0;
}

.form-section {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 12px;
  overflow: hidden;
  padding: 14px 16px 16px;
}

.form-section:last-child {
  margin-bottom: 0;
}

.form-section-header {
  display: flex;
  align-items: center;
  background: #eef2f7;
  padding: 8px 14px;
  font-weight: 700;
  font-size: 0.82rem;
  color: #3d4856;
  border-bottom: 1px solid #dce3eb;
  letter-spacing: 0.01em;
}

.form-section-body {
  background: #fafbfc;
}

/* SectionRenderer 내부 field-grid 간격 조정 */
.form-section-body :deep(.field-grid) {
  gap: 14px 16px;
}

.form-grid-2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 16px;
  align-items: start;
}

.directorship-question {
  padding: 4px 0;
}

.directorship-entry-form {
  padding: 8px 0;
}

.directorship-table {
  border: 1px solid #dce3eb;
  border-radius: 4px;
  overflow-x: auto;
}

.directorship-table-header {
  display: grid;
  grid-template-columns: 30px minmax(100px, 1.2fr) minmax(100px, 2fr) minmax(90px, 1fr) minmax(90px, 1fr) 36px;
  gap: 0;
  background: #3d4856;
  color: #fff;
  padding: 8px 12px;
  font-weight: 600;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.directorship-table-row {
  display: grid;
  grid-template-columns: 30px minmax(100px, 1.2fr) minmax(100px, 2fr) minmax(90px, 1fr) minmax(90px, 1fr) 36px;
  gap: 0;
  padding: 8px 12px;
  font-size: 0.8rem;
  border-bottom: 1px solid #eee;
  align-items: center;
}

.directorship-table-row:last-child {
  border-bottom: none;
}

.directorship-table-row:hover {
  background: #f5f8fb;
}

.dth-no {
  text-align: center;
}

.dth-regNo,
.dth-name,
.dth-appointed,
.dth-ceased {
  padding: 0 4px;
  word-break: break-word;
}

.dth-action {
  text-align: center;
}

.field-readonly {
  opacity: 0.75;
}

.field-readonly :deep(.v-field) {
  background: #f0f0f0 !important;
  border-color: #d5d5d5 !important;
}

.field-readonly :deep(.v-field__input),
.field-readonly :deep(.v-select__selection-text) {
  color: #666 !important;
}
</style>
